import { useMemo } from "react";
import { useAuth, useConfig } from "./provider";

export type OnStatus = (status: number, detail: string) => void;

/** fetch wrapper: bearer token, base path, 401 -> re-login, 403 -> notify, 409 -> notify. */
export function makeApiClient(basePath: string, getToken: () => Promise<string>, onStatus?: OnStatus) {
  return async (path: string, init: RequestInit = {}): Promise<Response> => {
    const token = await getToken();
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(basePath.replace(/\/$/, "") + path, { ...init, headers });
    if ([401, 403, 409].includes(res.status) && onStatus) {
      let detail = res.statusText;
      try { detail = (await res.clone().json()).detail ?? detail; } catch { /* ignore */ }
      onStatus(res.status, String(detail));
    }
    return res;
  };
}

export function useApiClient(onStatus?: OnStatus) {
  const auth = useAuth();
  const config = useConfig();
  return useMemo(() => makeApiClient(config.api_base, auth.getAccessToken, (status, detail) => {
    if (status === 401) auth.login();
    onStatus?.(status, detail);
  }), [config.api_base, auth, onStatus]);
}
